import { Loader2, Plus, Trash2, Pencil, BookOpen } from 'lucide-react'
import { RewriteChannel, RewriteGuideline } from '@/services/api'
import clsx from 'clsx'

interface GuidelineTabProps {
  channel: RewriteChannel | undefined
  sectionType: string
  sectionIndex: number
  channelGuidelines: Record<string, RewriteGuideline[]>
  selectedGuidelineIds: Record<string, Set<string>>
  guidelineEdits: Record<string, string>
  editingGuidelineId: string | null
  customGuidelines: Record<string, string[]>
  customGuidelineInput: Record<string, string>
  loadingGuidelines: Record<string, boolean>
  onSelectAll: (channelId: string) => void
  onDeselectAll: (channelId: string) => void
  onToggleGuideline: (channelId: string, guidelineId: string) => void
  onStartEditGuideline: (guidelineId: string, content: string) => void
  onStopEditGuideline: () => void
  onUpdateGuidelineEdit: (guidelineId: string, content: string) => void
  onResetGuidelineEdit: (guidelineId: string, originalContent: string) => void
  onAddCustomGuideline: (channelId: string) => void
  onRemoveCustomGuideline: (channelId: string, index: number) => void
  onSetCustomInput: (channelId: string, value: string) => void
}

export function GuidelineTab({
  channel, sectionType, sectionIndex,
  channelGuidelines, selectedGuidelineIds, guidelineEdits, editingGuidelineId,
  customGuidelines, customGuidelineInput, loadingGuidelines,
  onSelectAll, onDeselectAll, onToggleGuideline,
  onStartEditGuideline, onStopEditGuideline, onUpdateGuidelineEdit, onResetGuidelineEdit,
  onAddCustomGuideline, onRemoveCustomGuideline, onSetCustomInput,
}: GuidelineTabProps) {
  if (!channel) return <div className="p-4 text-sm text-text-tertiary">채널을 선택하세요.</div>

  const chId = channel.id
  const isLoading = loadingGuidelines[chId]
  const gls = channelGuidelines[chId] || []
  const selected = selectedGuidelineIds[chId] || new Set<string>()
  const customs = customGuidelines[chId] || []

  // Parse tone rules
  const toneRules = typeof channel.tone_rules === 'string' ? (() => { try { return JSON.parse(channel.tone_rules as string) } catch { return null } })() : channel.tone_rules
  const tone = toneRules as { tone?: string; rules?: string[] } | null

  // Parse section tone overrides
  const structure = typeof channel.structure === 'string' ? (() => { try { return JSON.parse(channel.structure as string) } catch { return null } })() : channel.structure
  const sections = (structure as { sections?: Array<{ type: string; toneOverrides?: { tone?: string; rules?: string[]; useEmoji?: boolean } }> })?.sections
  const sectionConfig = sections?.[sectionIndex]
  const toneOverride = sectionConfig?.toneOverrides

  // Filter guidelines: common (no section_type) + section-specific
  const commonGuidelines = gls.filter(g => !g.section_type)
  const sectionGuidelines = gls.filter(g => g.section_type === sectionType)

  return (
    <div className="p-4 space-y-4">
      {/* Channel tone */}
      {tone && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
          <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
            <BookOpen className="w-3 h-3" /> 채널 톤앤매너
          </h4>
          {tone.tone && <p className="text-sm font-medium mb-1">{tone.tone}</p>}
          {tone.rules && tone.rules.length > 0 && (
            <ul className="space-y-0.5">
              {tone.rules.map((r, i) => <li key={i} className="text-xs text-text-secondary">• {r}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Section tone override */}
      {toneOverride && (
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">
          <h4 className="text-xs font-bold text-purple-400 uppercase tracking-wide mb-1.5">
            섹션 톤 오버라이드
          </h4>
          {toneOverride.tone && <p className="text-sm font-medium mb-1">{toneOverride.tone}</p>}
          {toneOverride.rules && toneOverride.rules.length > 0 && (
            <ul className="space-y-0.5">
              {toneOverride.rules.map((r, i) => <li key={i} className="text-xs text-text-secondary">• {r}</li>)}
            </ul>
          )}
          {toneOverride.useEmoji !== undefined && (
            <p className="text-xs text-text-tertiary mt-1">이모지: {toneOverride.useEmoji ? '사용' : '미사용'}</p>
          )}
        </div>
      )}

      {/* Guidelines */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-text-secondary">가이드라인</h4>
          {gls.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={() => onSelectAll(chId)} className="text-xs text-primary hover:underline">전체</button>
              <span className="text-text-tertiary text-xs">|</span>
              <button onClick={() => onDeselectAll(chId)} className="text-xs text-text-tertiary hover:underline">해제</button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 py-4 justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-text-tertiary" />
            <span className="text-sm text-text-tertiary">로딩 중...</span>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Common guidelines */}
            {commonGuidelines.length > 0 && (
              <div>
                <span className="text-xs text-text-tertiary font-medium">공통</span>
                <div className="space-y-1.5 mt-1">
                  {commonGuidelines.map(g => (
                    <GuidelineItem
                      key={g.id}
                      guideline={g}
                      isSelected={selected.has(g.id)}
                      isEditing={editingGuidelineId === g.id}
                      editText={guidelineEdits[g.id]}
                      channelId={chId}
                      onToggle={onToggleGuideline}
                      onStartEdit={onStartEditGuideline}
                      onStopEdit={onStopEditGuideline}
                      onUpdateEdit={onUpdateGuidelineEdit}
                      onResetEdit={onResetGuidelineEdit}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Section-specific guidelines */}
            {sectionGuidelines.length > 0 && (
              <div>
                <span className="text-xs text-text-tertiary font-medium">섹션별</span>
                <div className="space-y-1.5 mt-1">
                  {sectionGuidelines.map(g => (
                    <GuidelineItem
                      key={g.id}
                      guideline={g}
                      isSelected={selected.has(g.id)}
                      isEditing={editingGuidelineId === g.id}
                      editText={guidelineEdits[g.id]}
                      channelId={chId}
                      onToggle={onToggleGuideline}
                      onStartEdit={onStartEditGuideline}
                      onStopEdit={onStopEditGuideline}
                      onUpdateEdit={onUpdateGuidelineEdit}
                      onResetEdit={onResetGuidelineEdit}
                    />
                  ))}
                </div>
              </div>
            )}

            {commonGuidelines.length === 0 && sectionGuidelines.length === 0 && (
              <p className="text-xs text-text-tertiary py-2">등록된 가이드라인 없음</p>
            )}
          </div>
        )}
      </div>

      {/* Custom guidelines */}
      <div>
        <h4 className="text-sm font-bold text-text-secondary mb-2">임시 가이드라인</h4>
        {customs.map((text, idx) => (
          <div key={idx} className="rounded border border-dashed border-primary/30 bg-primary/5 p-2 flex items-start gap-1.5 mb-1.5">
            <span className="text-xs text-primary font-medium">[임시]</span>
            <p className="text-xs flex-1">{text}</p>
            <button onClick={() => onRemoveCustomGuideline(chId, idx)} className="p-0.5 text-text-tertiary hover:text-red-400">
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <input
            type="text"
            value={customGuidelineInput[chId] || ''}
            onChange={(e) => onSetCustomInput(chId, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (customGuidelineInput[chId] || '').trim()) onAddCustomGuideline(chId)
            }}
            placeholder="임시 가이드라인 추가..."
            className="flex-1 bg-bg-elevated border border-border-subtle rounded px-2 py-1.5 text-xs"
          />
          <button onClick={() => onAddCustomGuideline(chId)} className="p-1.5 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function GuidelineItem({
  guideline, isSelected, isEditing, editText, channelId,
  onToggle, onStartEdit, onStopEdit, onUpdateEdit, onResetEdit,
}: {
  guideline: RewriteGuideline
  isSelected: boolean
  isEditing: boolean
  editText?: string
  channelId: string
  onToggle: (chId: string, gId: string) => void
  onStartEdit: (gId: string, content: string) => void
  onStopEdit: () => void
  onUpdateEdit: (gId: string, content: string) => void
  onResetEdit: (gId: string, original: string) => void
}) {
  return (
    <div className={clsx('rounded border p-2 transition-all', isSelected ? 'border-primary/30 bg-primary/5' : 'border-border-subtle opacity-60')}>
      <div className="flex items-start gap-1.5">
        <input type="checkbox" checked={isSelected} onChange={() => onToggle(channelId, guideline.id)} className="mt-0.5 accent-primary" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-xs font-medium truncate">{guideline.title}</span>
            <button
              onClick={() => { if (isEditing) onStopEdit(); else onStartEdit(guideline.id, guideline.content) }}
              className="p-0.5 text-text-tertiary hover:text-text-primary ml-auto flex-shrink-0"
            >
              <Pencil className="w-2.5 h-2.5" />
            </button>
          </div>
          {isEditing ? (
            <div className="mt-1">
              <textarea
                value={editText ?? guideline.content}
                onChange={(e) => onUpdateEdit(guideline.id, e.target.value)}
                rows={2}
                className="w-full bg-bg-elevated border border-border-subtle rounded px-2 py-1 text-xs resize-y"
              />
              <div className="flex gap-1 mt-0.5">
                <button onClick={onStopEdit} className="text-xs text-text-tertiary hover:text-text-primary">닫기</button>
                <button onClick={() => onResetEdit(guideline.id, guideline.content)} className="text-xs text-text-tertiary hover:text-text-primary">복원</button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-text-tertiary line-clamp-2">{editText ?? guideline.content}</p>
          )}
        </div>
      </div>
    </div>
  )
}
